package dev.danko.roblox_craft_ai.shared.model

/**
 * Titles and descriptions of the curated presets in the non-Russian interface languages.
 * Russian texts live in [PresetSeeds]; prompts stay in English because the AI works best with them.
 */
internal object PresetTranslations {
    private class Texts(val title: String, val description: String)

    private class Entry(val en: Texts, val uk: Texts, val de: Texts) {
        fun titles() = mapOf("en" to en.title, "uk" to uk.title, "de" to de.title)
        fun descriptions() = mapOf("en" to en.description, "uk" to uk.description, "de" to de.description)
    }

    private fun t(title: String, description: String) = Texts(title, description)

    private val ENTRIES: Map<String, Entry> = mapOf(
        "env-medieval-dungeon" to Entry(
            en = t("Medieval dungeon corridor", "Stone dungeon corridor with torches, barrels and an iron gate"),
            uk = t("Середньовічний коридор підземелля", "Кам'яний коридор підземелля зі смолоскипами, бочками й залізною брамою"),
            de = t("Mittelalterlicher Kerkergang", "Steinerner Kerkergang mit Fackeln, Fässern und einem Eisentor")
        ),
        "env-cyberpunk-alley" to Entry(
            en = t("Rainy cyberpunk alley", "Wet asphalt, neon signs and holographic ads in cyberpunk style"),
            uk = t("Кіберпанк-провулок під дощем", "Мокрий асфальт, неонові вивіски й голографічна реклама в стилі кіберпанк"),
            de = t("Cyberpunk-Gasse im Regen", "Nasser Asphalt, Neonschilder und Hologramm-Werbung im Cyberpunk-Stil")
        ),
        "env-cartoon-forest" to Entry(
            en = t("Cartoon forest clearing", "Bright fairy-tale clearing with cartoon trees and mushrooms"),
            uk = t("Мультяшна лісова галявина", "Яскрава казкова галявина з мультяшними деревами й грибами"),
            de = t("Comic-Waldlichtung", "Bunte Märchenlichtung mit Comic-Bäumen und Pilzen")
        ),
        "npc-orc-warrior" to Entry(
            en = t("Armored orc warrior", "Massive green orc in plate armor with a battle axe"),
            uk = t("Воїн-орк в обладунках", "Масивний зелений орк у латах із бойовою сокирою"),
            de = t("Ork-Krieger in Rüstung", "Wuchtiger grüner Ork in Plattenrüstung mit Streitaxt")
        ),
        "npc-friendly-merchant" to Entry(
            en = t("Friendly merchant NPC", "Smiling merchant in a robe with a backpack of goods and a coin pouch"),
            uk = t("Привітний торговець NPC", "Усміхнений торговець у рясі з рюкзаком товарів і гаманцем"),
            de = t("Freundlicher Händler-NPC", "Lächelnder Händler im Gewand mit Warenrucksack und Geldbeutel")
        ),
        "npc-evil-wizard-boss" to Entry(
            en = t("Dark wizard boss", "Sinister hooded mage with a glowing staff and a magic aura"),
            uk = t("Темний чарівник-бос", "Зловісний маг у каптурі зі світним посохом і магічною аурою"),
            de = t("Dunkler Zauberer-Boss", "Finsterer Magier mit Kapuze, leuchtendem Stab und magischer Aura")
        ),
        "int-rustic-kitchen" to Entry(
            en = t("Rustic village kitchen", "Cozy village kitchen with a fireplace, wooden furniture and utensils"),
            uk = t("Затишна сільська кухня", "Затишна сільська кухня з каміном, дерев'яними меблями й начинням"),
            de = t("Rustikale Dorfküche", "Gemütliche Dorfküche mit Kamin, Holzmöbeln und Küchengeräten")
        ),
        "int-throne-room" to Entry(
            en = t("Royal throne room", "Majestic throne room with marble pillars and a royal throne"),
            uk = t("Королівська тронна зала", "Велична тронна зала з мармуровими колонами й королівським троном"),
            de = t("Königlicher Thronsaal", "Prächtiger Thronsaal mit Marmorsäulen und königlichem Thron")
        ),
        "int-modern-bedroom" to Entry(
            en = t("Modern teen bedroom", "Stylish gamer bedroom with RGB lighting, a computer and posters"),
            uk = t("Сучасна кімната підлітка", "Стильна кімната геймера з RGB-підсвіткою, комп'ютером і плакатами"),
            de = t("Modernes Jugendzimmer", "Stylisches Gamer-Zimmer mit RGB-Beleuchtung, Computer und Postern")
        ),
        "wpn-legendary-sword" to Entry(
            en = t("Legendary fire-gem sword", "Jeweled knight's sword with a ruby and dragon-wing ornaments"),
            uk = t("Легендарний меч із вогняним каменем", "Коштовний лицарський меч із рубіном і орнаментом із драконячих крил"),
            de = t("Legendäres Schwert mit Feuerstein", "Edles Ritterschwert mit Rubin und Drachenflügel-Verzierungen")
        ),
        "wpn-scifi-plasma-rifle" to Entry(
            en = t("Sci-fi plasma rifle", "Futuristic weapon with neon energy coils and a scope"),
            uk = t("Фантастична плазмова гвинтівка", "Футуристична зброя з неоновими енергетичними котушками й оптикою"),
            de = t("Sci-Fi-Plasmagewehr", "Futuristische Waffe mit Neon-Energiespulen und Zielfernrohr")
        ),
        "wpn-wooden-hunter-bow" to Entry(
            en = t("Wooden hunting bow", "Simple carved wooden bow with a bowstring and leather grip"),
            uk = t("Дерев'яний мисливський лук", "Простий різьблений дерев'яний лук із тятивою й шкіряним обмотуванням"),
            de = t("Hölzerner Jagdbogen", "Schlichter geschnitzter Holzbogen mit Sehne und Ledergriff")
        ),
        "itm-legendary-chest" to Entry(
            en = t("Legendary treasure chest", "Ornate chest of jewels with a glowing gem in the lid"),
            uk = t("Легендарна скриня зі скарбами", "Оздоблена скриня з коштовностями й світним каменем у кришці"),
            de = t("Legendäre Schatztruhe", "Verzierte Truhe voller Juwelen mit leuchtendem Edelstein im Deckel")
        ),
        "itm-health-potion" to Entry(
            en = t("Red health potion", "Classic RPG health potion in a glass vial with red liquid"),
            uk = t("Червоне зілля здоров'я", "Класичне RPG-зілля здоров'я у скляній пляшечці з червоною рідиною"),
            de = t("Roter Heiltrank", "Klassischer RPG-Heiltrank in einem Glasfläschchen mit roter Flüssigkeit")
        ),
        "itm-crafted-pickaxe" to Entry(
            en = t("Miner's crystal pickaxe", "Powerful pickaxe with a crystal head and a wooden handle"),
            uk = t("Кришталеве кайло шахтаря", "Потужне кайло з кришталевим наконечником і дерев'яним руків'ям"),
            de = t("Kristall-Spitzhacke des Bergmanns", "Starke Spitzhacke mit Kristallkopf und Holzstiel")
        ),
        "anim-sword-combo" to Entry(
            en = t("3-hit sword combo", "Three-strike sword combo with forward root motion"),
            uk = t("Комбо-атака мечем на 3 удари", "Комбо-атака мечем із трьох ударів із рухом уперед"),
            de = t("Schwert-Kombo mit 3 Schlägen", "Schwert-Kombo aus drei Schlägen mit Vorwärtsbewegung")
        ),
        "anim-bat-m1-heavy-swing" to Entry(
            en = t("M1 heavy bat swing right to left", "Single heavy bat strike from right to left with zero clipping and a wide outer arc"),
            uk = t("M1 важкий удар битою справа наліво", "Одиночний важкий удар битою справа наліво без кліпінгу та з широкою зовнішньою дугою"),
            de = t("M1 schwerer Baseballschlaeger-Schwung rechts nach links", "Einzelner schwerer Schlag von rechts nach links ohne Clipping und mit breitem Aussenbogen")
        ),
        "anim-idle-breathing" to Entry(
            en = t("Idle breathing loop", "Natural idle loop with breathing, subtle head moves and blinking"),
            uk = t("Цикл дихання в спокої", "Природний цикл очікування з диханням, рухами голови й кліпанням"),
            de = t("Atem-Loop im Stand", "Natürlicher Warte-Loop mit Atmen, kleinen Kopfbewegungen und Blinzeln")
        ),
        "vfx-fireball-spell" to Entry(
            en = t("Fireball spell effect", "Fireball effect script with particles, light and an explosion on impact"),
            uk = t("Ефект закляття «вогняна куля»", "Скрипт ефекту вогняної кулі з частинками, світлом і вибухом при зіткненні"),
            de = t("Feuerball-Zaubereffekt", "Skript für einen Feuerball mit Partikeln, Licht und Explosion beim Aufprall")
        ),
        "scr-rpg-hotbar" to Entry(
            en = t("9-slot RPG inventory hotbar", "Full 9-slot hotbar with cooldowns, tooltips and drag and drop"),
            uk = t("RPG-хотбар інвентарю на 9 слотів", "Повноцінний хотбар на 9 слотів із кулдаунами, підказками й перетягуванням"),
            de = t("RPG-Inventarleiste mit 9 Plätzen", "Komplette Schnellleiste mit 9 Plätzen, Abklingzeiten, Tooltips und Drag & Drop")
        ),
        "scr-damage-popup" to Entry(
            en = t("Floating damage numbers", "Pop-up damage numbers with red critical hits and a bounce animation"),
            uk = t("Спливаючі числа шкоди", "Спливаючі числа шкоди з червоними критичними ударами й анімацією підстрибування"),
            de = t("Schwebende Schadenszahlen", "Aufpoppende Schadenszahlen mit roten kritischen Treffern und Hüpf-Animation")
        ),
        "scr-quest-dialog" to Entry(
            en = t("Quest dialogue window", "NPC dialogue window with a portrait, typewriter effect and accept/decline buttons"),
            uk = t("Діалогове вікно квестів", "Вікно діалогу NPC з портретом, ефектом друкарської машинки й кнопками прийняти/відхилити"),
            de = t("Quest-Dialogfenster", "NPC-Dialogfenster mit Porträt, Schreibmaschinen-Effekt und Annehmen/Ablehnen-Knöpfen")
        )
    )

    fun applyTo(presets: List<PromptPresetDto>): List<PromptPresetDto> = presets.map { preset ->
        val entry = ENTRIES[preset.id] ?: return@map preset
        preset.copy(titleI18n = entry.titles(), descriptionI18n = entry.descriptions())
    }

    fun hasTranslation(id: String): Boolean = id in ENTRIES
}
